import React, { useState } from "react";
import { useLiveEdit } from "../../lib/liveEdit";
import { HOME_SECTIONS, sectionKeys } from "../../lib/sectionRegistry";
import { Plus, RotateCcw, Check, X, EyeOff, LayoutList } from "lucide-react";

/**
 * Section adder — shown only in edit mode. Lists sections currently hidden
 * (or not in the order) so admin can bring them back onto the page.
 */
export default function SectionAdder() {
  const { editMode, isAdmin, sections, showSection, resetSections } = useLiveEdit();
  const [open, setOpen] = useState(false);
  if (!editMode || !isAdmin) return null;

  const all = sectionKeys();
  const inUse = new Set(sections.order);
  const hidden = all.filter((k) => !inUse.has(k));

  return (
    <div className="relative py-16 bg-gradient-to-b from-transparent to-yellow-50/40" data-testid="section-adder">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <div className="rounded-3xl border-2 border-dashed border-yellow-400/70 bg-white/80 backdrop-blur p-8 text-center">
          <div className="mx-auto h-12 w-12 rounded-2xl bg-yellow-400 text-[#0B2A6B] flex items-center justify-center">
            <LayoutList className="h-6 w-6" />
          </div>
          <h3 className="mt-4 text-lg font-bold text-[#0B2A6B]">Manage home page sections</h3>
          <p className="text-sm text-slate-600 mt-1">
            You have <strong>{sections.order.length}</strong> section{sections.order.length === 1 ? "" : "s"} visible
            {hidden.length > 0 && <> and <strong>{hidden.length}</strong> hidden</>}.
          </p>

          {hidden.length > 0 && (
            <div className="mt-6">
              {!open ? (
                <button
                  type="button"
                  onClick={() => setOpen(true)}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#0B2A6B] text-white font-bold hover:bg-[#061a47]"
                  data-testid="section-adder-open"
                >
                  <Plus className="h-4 w-4" /> Add a section
                </button>
              ) : (
                <div className="mt-2 grid sm:grid-cols-2 gap-2 text-left">
                  {hidden.map((k) => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => { showSection(k); }}
                      className="flex items-center justify-between px-4 py-3 rounded-xl border border-slate-200 bg-white hover:border-yellow-400 hover:bg-yellow-50 transition"
                      data-testid={`section-add-${k}`}
                    >
                      <span className="font-semibold text-[#0B2A6B]">{HOME_SECTIONS[k].label}</span>
                      <Plus className="h-4 w-4 text-yellow-500" />
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="col-span-full text-xs text-slate-500 hover:text-slate-700 mt-1"
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
          )}

          <button
            type="button"
            onClick={() => {
              if (window.confirm("Reset home page sections to the default order? This will unhide hidden sections but keep your text edits.")) {
                resetSections();
                setOpen(false);
              }
            }}
            className="mt-4 inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-[#0B2A6B]"
            data-testid="section-reset"
          >
            <RotateCcw className="h-3.5 w-3.5" /> Reset to default layout
          </button>
        </div>
      </div>
    </div>
  );
}
